// First import: points the store-free app-store port at the live store before
// any other module can resolve it (see store/installBrowserAppRuntime.ts).
import "./store/installBrowserAppRuntime";
import React, { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import App from "./App.tsx";
import { AuthGate } from "./components/AuthGate.tsx";
import {
  InviteLanding,
  urlLooksLikeMagicLink,
} from "./components/InviteLanding.tsx";
import {
  PasswordResetScreen,
  urlLooksLikePasswordReset,
} from "./components/auth/PasswordResetScreen.tsx";
import { retryImport } from "./components/siterag/docViewers/retryImport.tsx";
// R-104 chunk-cycle fix: ReportQualityGuidePage is EAGER-imported (not lazy).
// As a lazy() route its emitted chunk was a *pure leaf* of the entry chunk —
// its only first-party static import was the entry chunk itself (via the
// store + UI primitives), so Vite produced a dynamic import() with an EMPTY
// __vite__mapDeps([]) preload list and the resolved module namespace was
// `undefined` at import time → prod crash on /guide/reports:
// "Cannot read properties of undefined (reading 'ReportQualityGuidePage')".
// manualChunks splits were tried but hoisted shared code unpredictably; a
// static import folds the (tiny, ~7 KB) page into the entry chunk whose heavy
// deps (store, UI primitives) it already shares — deterministic, crash-proof,
// negligible weight. See R104LazyImports.smoke.test.ts + the regression test.
import { ReportsGuideUnavailablePage } from "./components/reportGuide/ReportsGuideUnavailablePage";
import { RouteLoadingSkeleton } from "./components/ui/RouteLoadingSkeleton";
import { HelpProvider } from "./components/help/HelpProvider";
import { applyThemePreference, getStoredThemePreference } from "./lib/theme.ts";
import {
  applyResolvedAccent,
  getStoredAccentPalette,
} from "./lib/accentPalette.ts";
import { initScrollGlow } from "./lib/scrollGlow.ts";
import { persistCrashTelemetry } from "./services/crashTelemetry.ts";
import { installRechartsWarnFilter } from "./lib/rechartsWarnFilter.ts";
import { handleAuthRedirect } from "./firebase.ts";
import { mountWithBoundedAuthRedirect } from "./lib/auth/initialAuthRedirect";
import "./index.css";
// Aurora Cockpit — the shared `.da-*` tab design system (FOUNDATION wave).
// References the tokens declared in index.css (:root / .light / [data-accent]),
// so it themes automatically across dark/light + all 4 accents. Imported once,
// app-wide; surfaces opt in by using the classes / primitives.
import "./styles/auroraCockpit.css";
import "./lib/telemetry/tracedGeminiCall";
import { isEoEngineActive } from "./lib/sim/eo/eoEngineFlag";

// RCD-3 — install the recharts dimension-warning filter before any
// chart-rendering chunk loads. SafeResponsiveContainer's first-paint
// gate prevents the warning for in-app callsites, but lazy chunks and
// the print-mode pre-mount pass can still trip recharts' own check
// before our gate observes a size. Filter the noise here so real
// console errors stay visible. Idempotent — subsequent imports during
// HMR / tests are no-ops.
installRechartsWarnFilter();

const SharedReportView = lazy(
  retryImport(() =>
    import("./components/SharedReportView.tsx").then((m) => ({
      default: m.SharedReportView,
    })),
  ),
);
const PrintReportView = lazy(
  retryImport(() =>
    import("./components/PrintReportView.tsx").then((m) => ({
      default: m.PrintReportView,
    })),
  ),
);
const SampleReportPrintView = lazy(
  retryImport(() =>
    import("./components/SampleReportPrintView.tsx").then((m) => ({
      default: m.SampleReportPrintView,
    })),
  ),
);
// DEFECT-4 — internal LIVE-viewer render route. Mounts the continuous report
// inside the EXACT SiteRagEngine flex/overflow ancestor chain so the harness
// can assert real pagination (vs the continuous fallback) the export route
// never reproduced. Same out-of-auth-shell discipline as /internal/sample-pdf.
const SampleLiveView = lazy(
  retryImport(() =>
    import("./components/SampleLiveView.tsx").then((m) => ({
      default: m.SampleLiveView,
    })),
  ),
);
// Per-company branding editor. Replaces the retired Template Studio —
// report DESIGN is now locked to the golden stock template; only the
// company name / brand colour / logo remain user-editable.
const BrandSettingsPanel = lazy(
  retryImport(() =>
    import("./components/BrandSettingsPanel").then((m) => ({
      default: m.BrandSettingsPanel,
    })),
  ),
);
const CompanySuppliersPage = lazy(
  retryImport(() =>
    import("./components/company/CompanySuppliersPage").then((m) => ({
      default: m.CompanySuppliersPage,
    })),
  ),
);
const CustomerPortfolio = lazy(
  retryImport(() =>
    import("./pages/CustomerPortfolio").then((m) => ({
      default: m.CustomerPortfolio,
    })),
  ),
);
const DocViewer = lazy(retryImport(() => import("./pages/DocViewer")));
const SimulationViewer = lazy(
  retryImport(() => import("./pages/SimulationViewer")),
);
const SimulationProjectsPage = lazy(
  retryImport(() =>
    import("./pages/SimulationProjectsPage").then((m) => ({
      default: m.SimulationProjectsPage,
    })),
  ),
);
const ProjectModelEditorRoute = lazy(
  retryImport(() =>
    import("./pages/ProjectModelEditorRoute").then((m) => ({
      default: m.ProjectModelEditorRoute,
    })),
  ),
);
const SimulationRevisionReviewRoute = lazy(
  retryImport(() =>
    import("./pages/SimulationRevisionReviewRoute").then((m) => ({
      default: m.SimulationRevisionReviewRoute,
    })),
  ),
);
const SimulationHydraulicRunComparisonRoute = lazy(
  retryImport(() =>
    import("./pages/SimulationHydraulicRunComparisonRoute").then((m) => ({
      default: m.SimulationHydraulicRunComparisonRoute,
    })),
  ),
);
const SimulationGraphV3SteadyRunComparisonRoute = lazy(
  retryImport(() =>
    import("./pages/SimulationGraphV3SteadyRunComparisonRoute").then((m) => ({
      default: m.SimulationGraphV3SteadyRunComparisonRoute,
    })),
  ),
);
const HydraulicProfileWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/HydraulicProfileWorkbenchAuthRoute").then((m) => ({
      default: m.HydraulicProfileWorkbenchAuthRoute,
    })),
  ),
);
const SensibleWaterHeatPickupWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SensibleWaterHeatPickupWorkbenchAuthRoute").then((m) => ({
      default: m.SensibleWaterHeatPickupWorkbenchAuthRoute,
    })),
  ),
);
const TwoStreamSensibleHeatExchangerWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/TwoStreamSensibleHeatExchangerWorkbenchAuthRoute").then(
      (m) => ({
        default: m.TwoStreamSensibleHeatExchangerWorkbenchAuthRoute,
      }),
    ),
  ),
);
const SteadyDlcThermalLoopWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyDlcThermalLoopWorkbenchAuthRoute").then((m) => ({
      default: m.SteadyDlcThermalLoopWorkbenchAuthRoute,
    })),
  ),
);
const SteadyDlcPressureThermalLoopWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyDlcPressureThermalLoopWorkbenchAuthRoute").then(
      (m) => ({
        default: m.SteadyDlcPressureThermalLoopWorkbenchAuthRoute,
      }),
    ),
  ),
);
const IdealConstantCpFluidJunctionWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/IdealConstantCpFluidJunctionWorkbenchAuthRoute").then(
      (m) => ({
        default: m.IdealConstantCpFluidJunctionWorkbenchAuthRoute,
      }),
    ),
  ),
);
const SteadyConstantCpNetworkWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyConstantCpNetworkWorkbenchAuthRoute").then((m) => ({
      default: m.SteadyConstantCpNetworkWorkbenchAuthRoute,
    })),
  ),
);
const SteadyConstantCpComposableAdiabaticNetworkWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyConstantCpNetworkWorkbenchAuthRoute").then((m) => ({
      default: m.SteadyConstantCpComposableAdiabaticNetworkWorkbenchAuthRoute,
    })),
  ),
);
const SteadyConstantCpPipeUaNetworkWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyConstantCpPipeUaNetworkWorkbenchAuthRoute").then(
      (m) => ({
        default: m.SteadyConstantCpPipeUaNetworkWorkbenchAuthRoute,
      }),
    ),
  ),
);
const SteadyConstantCpPumpedPipeUaNetworkWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyConstantCpPumpedPipeUaNetworkWorkbenchAuthRoute").then(
      (m) => ({
        default: m.SteadyConstantCpPumpedPipeUaNetworkWorkbenchAuthRoute,
      }),
    ),
  ),
);
const SteadyConstantCpControlledPumpedPipeUaNetworkWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SteadyConstantCpControlledPumpedPipeUaNetworkWorkbenchAuthRoute").then(
      (m) => ({
        default:
          m.SteadyConstantCpControlledPumpedPipeUaNetworkWorkbenchAuthRoute,
      }),
    ),
  ),
);
const SteadyConstantCpCheckValvedControlledPumpedPipeUaNetworkWorkbenchAuthRoute =
  lazy(
    retryImport(() =>
      import("./pages/SteadyConstantCpCheckValvedControlledPumpedPipeUaNetworkWorkbenchAuthRoute").then(
        (m) => ({
          default:
            m.SteadyConstantCpCheckValvedControlledPumpedPipeUaNetworkWorkbenchAuthRoute,
        }),
      ),
    ),
  );
const ElectricalDcWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/ElectricalDcWorkbenchAuthRoute").then((m) => ({
      default: m.ElectricalDcWorkbenchAuthRoute,
    })),
  ),
);
const SampledClosedLoopWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/SampledClosedLoopWorkbenchAuthRoute").then((m) => ({
      default: m.SampledClosedLoopWorkbenchAuthRoute,
    })),
  ),
);
const FirstOrderActuatorWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/FirstOrderActuatorWorkbenchAuthRoute").then((m) => ({
      default: m.FirstOrderActuatorWorkbenchAuthRoute,
    })),
  ),
);
const CoupledNuclearWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/CoupledNuclearWorkbenchAuthRoute").then((m) => ({
      default: m.CoupledNuclearWorkbenchAuthRoute,
    })),
  ),
);
const ThermalFeedbackWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/ThermalFeedbackWorkbenchAuthRoute").then((m) => ({
      default: m.ThermalFeedbackWorkbenchAuthRoute,
    })),
  ),
);
const MechanicalShaftWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/MechanicalShaftWorkbenchAuthRoute").then((m) => ({
      default: m.MechanicalShaftWorkbenchAuthRoute,
    })),
  ),
);
const RotatingInertiaWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/RotatingInertiaWorkbenchAuthRoute").then((m) => ({
      default: m.RotatingInertiaWorkbenchAuthRoute,
    })),
  ),
);
const DcElectromechanicalWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/DcElectromechanicalWorkbenchAuthRoute").then((m) => ({
      default: m.DcElectromechanicalWorkbenchAuthRoute,
    })),
  ),
);
const DcShaftCoupledTransientWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/DcShaftCoupledTransientWorkbenchAuthRoute").then((m) => ({
      default: m.DcShaftCoupledTransientWorkbenchAuthRoute,
    })),
  ),
);
const BalancedAcPassiveFeederWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/BalancedAcPassiveFeederWorkbenchAuthRoute").then((m) => ({
      default: m.BalancedAcPassiveFeederWorkbenchAuthRoute,
    })),
  ),
);
const LumpedFluidInventoryWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/LumpedFluidInventoryWorkbenchAuthRoute").then((m) => ({
      default: m.LumpedFluidInventoryWorkbenchAuthRoute,
    })),
  ),
);
const LumpedFluidInventoryTimestepConvergenceWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/LumpedFluidInventoryTimestepConvergenceWorkbenchAuthRoute").then(
      (m) => ({
        default: m.LumpedFluidInventoryTimestepConvergenceWorkbenchAuthRoute,
      }),
    ),
  ),
);
const PrimarySecondaryThermalCouplingWorkbenchAuthRoute = lazy(
  retryImport(() =>
    import("./pages/PrimarySecondaryThermalCouplingWorkbenchAuthRoute").then(
      (m) => ({
        default: m.PrimarySecondaryThermalCouplingWorkbenchAuthRoute,
      }),
    ),
  ),
);
const SimulationPreviewLabsPage = lazy(
  retryImport(() =>
    import("./pages/SimulationPreviewLabsPage").then((m) => ({
      default: m.SimulationPreviewLabsPage,
    })),
  ),
);
const MalformedProjectModelRoute = lazy(
  retryImport(() =>
    import("./pages/ProjectModelEditorRoute").then((m) => ({
      default: m.MalformedProjectModelRoute,
    })),
  ),
);
// Restored 2026-05-31 (f7bb7a69 accidental revert): self-serve account deletion
// + the public status page.
const DeleteAccountPage = lazy(
  retryImport(() =>
    import("./pages/DeleteAccountPage").then((m) => ({
      default: m.DeleteAccountPage,
    })),
  ),
);
// Restored 2026-06-10 (f7bb7a69 accidental revert): the /help knowledge-base
// routes (R-131-SUP-C). Without them the Cmd+K palette's Enter-to-open, the
// help drawer's "Open full page" link, and all cross-article markdown links
// fell through `/*` to the dashboard. Lazy is SAFE here (unlike the guide's
// R-104 case): the HelpPage chunk statically imports react-markdown +
// remark-gfm, so it is not a pure leaf of the entry chunk.
const HelpPage = lazy(
  retryImport(() =>
    import("./components/help/HelpPage").then((m) => ({ default: m.HelpPage })),
  ),
);

// v1.12.8 — PUBLIC legal docs (privacy + terms). Lazy-loaded and rendered in the
// OUT-OF-AUTH-SHELL area below (same discipline as /print, /internal/sample-pdf):
// outside AuthGate so they are viewable WITHOUT a session, but inside their own
// BrowserRouter so the pages' react-router <Link> cross-links (Home / Privacy /
// Terms) resolve. NOT placed inside the AuthGate-wrapped <AppRoutes>.
const PrivacyPage = lazy(
  retryImport(() =>
    import("./components/marketing/PrivacyPage").then((m) => ({
      default: m.PrivacyPage,
    })),
  ),
);
const TermsPage = lazy(
  retryImport(() =>
    import("./components/marketing/TermsPage").then((m) => ({
      default: m.TermsPage,
    })),
  ),
);

/**
 * Exact-release simulation freeze boundary.
 *
 * The frontend release may intentionally ship with VITE_EO_ENGINE=off while
 * the last-green simulation deployment remains untouched. Direct editor and
 * project links must therefore fail closed instead of advertising controls
 * whose mutations the server rejects. Site, Findings, Decision, reports, and
 * Generation & Impact remain available through the normal application shell.
 */
function SimulationComputeDisabledRoute(): React.ReactElement {
  return (
    <main
      className="min-h-screen bg-background px-6 py-16 text-text-primary"
      data-testid="simulation-compute-disabled-route"
    >
      <section className="mx-auto max-w-xl rounded-xl border border-border bg-card p-6 shadow-card">
        <p className="type-meta uppercase tracking-widest text-text-muted">
          Engineering Analysis
        </p>
        <h1 className="mt-2 text-xl font-semibold">Simulation compute is temporarily paused</h1>
        <p className="mt-3 type-body text-text-secondary">
          Existing site data, Analyses, Findings, Decision, Stakeholder Brief,
          Audit, and Generation &amp; Impact remain available. Simulation authoring
          and runs will return after the simulation release is revalidated.
        </p>
        <Link
          className="mt-5 inline-flex h-9 items-center rounded-md border border-primary/50 bg-primary/10 px-4 type-meta font-semibold uppercase tracking-wider text-primary hover:bg-primary/15"
          to="/"
        >
          Return to DataCrate
        </Link>
      </section>
    </main>
  );
}

/**
 * AP-routing — top-level route table.
 *
 * Only `/admin/*` is genuinely routed today (AdminLayout/AdminSidebar consume
 * `useLocation()`). Every other path falls through to `<App />` unchanged —
 * the rest of the app is still navigated via Zustand `viewMode` / internal
 * state. Both branches render the same `<App />` component because:
 *   1. AdminPanel is a fullscreen overlay AdminPanel mounts INSIDE App.
 *   2. App also owns Zustand-hydration side-effects (autosave, SiteRagEngine)
 *      that the admin surface relies on for live counts.
 * Splitting the two routes into separate roots would force a second mount
 * of those engines. App now derives `isAdminPanelOpen` from the URL.
 *
 * The three explicit `/admin` patterns match the AP1 sidebar contract:
 *   /admin                       → activeKey = 'now'
 *   /admin/:section              → activeKey = :section
 *   /admin/:section/:subsection  → reserved for future drill-down pages
 */
function AppRoutes() {
  const simulationComputeEnabled = isEoEngineActive();

  return (
    <HelpProvider>
      <Suspense fallback={<RouteLoadingSkeleton />}>
        <Routes>
          {/* R-130-5: band/cluster/ops now resolve through /admin/:section → <App /> so AdminPanel sidebar always mounts. */}
          <Route path="/admin" element={<App />} />
          <Route path="/admin/:section" element={<App />} />
          <Route path="/admin/:section/:subsection" element={<App />} />
          {/* Per-company branding editor. AuthGate already wraps
           *  <BrowserRouter><AppRoutes/> above, so this route inherits
           *  authentication automatically. */}
          <Route path="/settings/branding" element={<BrandSettingsPanel />} />
          <Route path="/company/suppliers" element={<CompanySuppliersPage />} />
          <Route
            path="/customers/:customerId/portfolio"
            element={<CustomerPortfolio />}
          />
          <Route path="/guide/reports" element={<ReportsGuideUnavailablePage />} />
          {/* R-131-SUP-C — in-app help knowledge base (restored, see note above). */}
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:slug" element={<HelpPage />} />
          {/* RWX-5 — rerun route. The route is a URL marker only; the
           *  underlying surface (FeasibilityRerunPanel-as-modal) is owned
           *  by SiteRagReportView which reads `useParams()` /
           *  `useLocation()` to open the panel when the URL matches.
           *  Falling through to <App /> keeps the auth shell + the
           *  store-driven view mode intact (per RWX pass-2 #12 — coexist
           *  with viewMode, no separate root mount). The path mirrors the
           *  pack spec; `?step=preflight|config|confirm` continues to
           *  drive the modal's tab via `useUrlSearchParamSync('step', …)`
          *  already in place since SC2-6. */}
          <Route path="/docs/:docId" element={<DocViewer />} />
          {simulationComputeEnabled ? (
            <>
          <Route path="/sims/:simId" element={<SimulationViewer />} />
          {/* C7 — neutral simulation-editor route. The simulation canvas is
           *  driven by Zustand `viewMode`, but the two App URL-sync effects
           *  snap viewMode back to siterag/dashboard whenever the path is
           *  site-scoped or a root/dashboard entry. `/editor` is NEITHER, so
           *  it lets `setViewMode('simulation')` survive. Falls through to
           *  <App /> (same element as `/`) so the app shell stays mounted. */}
          <Route path="/editor" element={<App />} />
          {import.meta.env.DEV ? (
            <Route
              path="/editor/preview-labs"
              element={<SimulationPreviewLabsPage />}
            />
          ) : null}
          <Route
            path="/editor/hydraulic-v3"
            element={<HydraulicProfileWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/sensible-water-heat-pickup-v3"
            element={<SensibleWaterHeatPickupWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/two-stream-sensible-hx-v3"
            element={<TwoStreamSensibleHeatExchangerWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/steady-dlc-thermal-loop-v3"
            element={<SteadyDlcThermalLoopWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/steady-dlc-pressure-thermal-loop-v3"
            element={<SteadyDlcPressureThermalLoopWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/ideal-constant-cp-fluid-junction-v3"
            element={<IdealConstantCpFluidJunctionWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/steady-constant-cp-network-v3"
            element={<SteadyConstantCpNetworkWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/steady-constant-cp-composable-adiabatic-network-v3"
            element={
              <SteadyConstantCpComposableAdiabaticNetworkWorkbenchAuthRoute />
            }
          />
          <Route
            path="/editor/steady-constant-cp-pipe-ua-network-v3"
            element={<SteadyConstantCpPipeUaNetworkWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/steady-constant-cp-pumped-pipe-ua-network-v3"
            element={<SteadyConstantCpPumpedPipeUaNetworkWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/steady-constant-cp-controlled-pumped-pipe-ua-network-v3"
            element={
              <SteadyConstantCpControlledPumpedPipeUaNetworkWorkbenchAuthRoute />
            }
          />
          <Route
            path="/editor/steady-constant-cp-check-valved-controlled-pumped-pipe-ua-network-v3"
            element={
              <SteadyConstantCpCheckValvedControlledPumpedPipeUaNetworkWorkbenchAuthRoute />
            }
          />
          <Route
            path="/editor/electrical-dc"
            element={<ElectricalDcWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/sampled-controls"
            element={<SampledClosedLoopWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/first-order-actuator-preview"
            element={<FirstOrderActuatorWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/nuclear-kinetics-preview"
            element={<CoupledNuclearWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/nuclear-thermal-feedback-preview"
            element={<ThermalFeedbackWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/mechanical-shaft-preview"
            element={<MechanicalShaftWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/mechanical-rotating-inertia-preview"
            element={<RotatingInertiaWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/dc-electromechanical-preview"
            element={<DcElectromechanicalWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/dc-shaft-coupled-transient-preview"
            element={<DcShaftCoupledTransientWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/balanced-ac-passive-feeder-preview"
            element={<BalancedAcPassiveFeederWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/lumped-fluid-inventory-transient-preview"
            element={<LumpedFluidInventoryWorkbenchAuthRoute />}
          />
          <Route
            path="/editor/lumped-fluid-inventory-timestep-convergence-preview"
            element={
              <LumpedFluidInventoryTimestepConvergenceWorkbenchAuthRoute />
            }
          />
          <Route
            path="/editor/primary-secondary-thermal-coupling-preview"
            element={<PrimarySecondaryThermalCouplingWorkbenchAuthRoute />}
          />
          <Route path="/projects" element={<SimulationProjectsPage />} />
          <Route
            path="/projects/:projectId/models/:modelId/revisions/:revisionId/review"
            element={<SimulationRevisionReviewRoute />}
          />
          <Route
            path="/projects/:projectId/models/:modelId/runs/:baselineRunId/compare/:candidateRunId"
            element={<SimulationHydraulicRunComparisonRoute />}
          />
          <Route
            path="/projects/:projectId/models/:modelId/graph-v3-steady-runs/:baselineRunId/compare/:candidateRunId"
            element={<SimulationGraphV3SteadyRunComparisonRoute />}
          />
          <Route
            path="/projects/:projectId/models/:modelId"
            element={<ProjectModelEditorRoute />}
          />
          <Route path="/projects/*" element={<MalformedProjectModelRoute />} />
            </>
          ) : (
            <>
              <Route path="/sims/*" element={<SimulationComputeDisabledRoute />} />
              <Route path="/editor/*" element={<SimulationComputeDisabledRoute />} />
              <Route path="/projects/*" element={<SimulationComputeDisabledRoute />} />
            </>
          )}
          <Route path="/issues" element={<App />} />
          <Route path="/issues/:bugReportId" element={<App />} />
          <Route
            path="/sites/:siteId/reports/:reportId/rerun"
            element={<App />}
          />
          <Route path="/sites/:siteId/reports/:reportId/summary" element={<App />} />
          <Route path="/sites/:siteId/reports/:reportId" element={<App />} />
          <Route path="/sites/:siteId" element={<App />} />
          <Route
            path="/profile/delete-account"
            element={<DeleteAccountPage />}
          />
          <Route path="/*" element={<App />} />
        </Routes>
      </Suspense>
    </HelpProvider>
  );
}

declare const __BUILD_HASH__: string;
// Stamp the build hash on window early so any crash telemetry written
// later carries it. The constant is injected by Vite (vite.config.ts
// `define`); fall back to 'unknown' for envs (eg. tests) that omit it.
try {
  (window as unknown as { __BUILD_HASH__?: string }).__BUILD_HASH__ =
    typeof __BUILD_HASH__ === "string" ? __BUILD_HASH__ : "unknown";
} catch {
  /* ignore */
}

/** R-82 — ring buffer of recent console errors for bug reports. */
function installConsoleErrorRingBuffer(): void {
  const win = window as unknown as { lastConsoleErrors?: string[] };
  win.lastConsoleErrors = win.lastConsoleErrors ?? [];
  const push = (line: string) => {
    win.lastConsoleErrors!.push(line.slice(0, 2000));
    if (win.lastConsoleErrors!.length > 20) {
      win.lastConsoleErrors = win.lastConsoleErrors!.slice(-20);
    }
  };
  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      push(
        args
          .map((a) =>
            a instanceof Error ? `${a.name}: ${a.message}` : String(a),
          )
          .join(" "),
      );
    } catch {
      /* ignore */
    }
    origError(...args);
  };
  window.addEventListener("error", (ev) => {
    push(`window.error: ${ev.message} @ ${ev.filename}:${ev.lineno}`);
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason =
      ev.reason instanceof Error ? ev.reason.message : String(ev.reason);
    push(`unhandledrejection: ${reason}`);
  });
}
installConsoleErrorRingBuffer();

// Apply the persisted theme BEFORE React mounts so the very first paint —
// including the login page — uses the right palette. Without this the
// AuthGate flashes in dark for the ~50–100ms it takes the ThemeToggle's
// useEffect (mounted deep in the authenticated shell) to fire.
applyThemePreference(getStoredThemePreference());

// Apply the persisted APP ACCENT palette before React mounts (mirrors the
// theme apply above + the index.html inline bootstrap), so the first paint
// already carries the user's chosen accent with no teal flash. Default
// (teal) removes the attribute, so default users' DOM is byte-identical.
applyResolvedAccent(getStoredAccentPalette());

// Start the accent scroll-glow: stamps `is-scrolling` on <html> while any
// container scrolls so the token-driven scrollbar (index.css) brightens +
// pulses. One capture-phase listener covers every nested scroller.
initScrollGlow();

// Check for shared report URL (/shared/{shareId})
const sharedMatch = window.location.pathname.match(/^\/shared\/(.+)$/);
const sharedReportId = sharedMatch?.[1] || null;

// Check for print-mode URL (/print/{reportId}). The server-side
// PDF renderer (POST /api/reports/render-pdf) spawns headless
// Chromium and points it here. The route is gated by a short-lived
// HMAC token in the query string — the server signs it before
// launch; client-side we just need to mount the report tree with no
// app chrome so puppeteer's waitForSelector('[data-report-ready]')
// fires once the report is settled.
const printMatch = window.location.pathname.match(/^\/print\/(.+)$/);
const printReportId = printMatch?.[1]
  ? decodeURIComponent(printMatch[1])
  : null;

// TSE-3-FOLLOWUP-SERVER-SAMPLE-PDF — internal sample-PDF route
// spawned by `POST /api/template-studio/export-sample-pdf`. Mirrors
// the `/print/{reportId}` probe above: synchronous path check at
// load so the SampleReportPrintView mounts outside the router and
// outside AuthGate (puppeteer can't carry a Firebase session). The
// route is gated by a short-lived HMAC token in the query string.
const isInternalSamplePdf = window.location.pathname === "/internal/sample-pdf";

// DEFECT-4 — internal LIVE-viewer render route (`/internal/sample-live`).
// Same synchronous-probe + out-of-auth-shell discipline as
// `/internal/sample-pdf`: the harness drives it to verify the on-screen
// report viewer (the continuous report in the SiteRagEngine ancestor chain).
const isInternalSampleLive =
  window.location.pathname === "/internal/sample-live";

// v1.12.8 — PUBLIC legal-doc routes. Synchronous path probe (mirrors the print /
// sample-pdf probes above) so the privacy/terms pages mount OUTSIDE AuthGate and
// are viewable with no session. Rendered in their own BrowserRouter <Routes>
// below so the pages' <Link> cross-links work.
const isPublicLegalRoute =
  window.location.pathname === "/privacy" ||
  window.location.pathname === "/terms";

// RRQ — FORCE LIGHT on every PDF/print render surface. The app boots DARK by
// default (`.light` is the override class; its absence leaves html/body at the
// near-black `--canvas`). The /print new-tab Export-to-PDF mounts the report
// under `[data-theme-scope="light"]`, but that only re-skins tokens INSIDE the
// report card — the surrounding page surface (html/body, the print frame, the
// @page background) inherited the app's dark canvas, so anything not painted by
// the report card showed SOLID BLACK (the user's "/print renders black, 1
// page"). Adding `.light` to <html> + a white background flips the whole page
// surface to paper-white, independent of the user's stored theme, so the export
// always renders on white. Also covers the internal sample-pdf / sample-live
// harness routes (same headless render shell). The server Puppeteer path is
// unaffected (headless Chromium has no stored dark preference) but benefits too.
const isPrintLikeRoute =
  !!printReportId || isInternalSamplePdf || isInternalSampleLive;
if (isPrintLikeRoute && typeof document !== "undefined") {
  try {
    const root = document.documentElement;
    root.classList.add("light");
    // White paper surface behind the report card, in screen + print. Set on
    // the element directly (not a class) so it wins regardless of cascade and
    // applies before React mounts (no black flash). `color-scheme: light`
    // keeps form controls / scrollbars light too.
    root.style.colorScheme = "light";
    root.style.background = "#ffffff";
    if (document.body) {
      document.body.style.background = "#ffffff";
    }
  } catch {
    /* defensive — never block the render on a style write */
  }
}

// Synchronous URL probe — if the current URL is a Firebase email-action-link
// callback, we route through InviteLanding which completes the sign-in
// handshake + invite acceptance before letting AuthGate take over.
const isMagicLinkLanding =
  !sharedReportId &&
  !printReportId &&
  !isInternalSamplePdf &&
  !isInternalSampleLive &&
  !isPublicLegalRoute &&
  urlLooksLikeMagicLink();
const isPasswordResetLanding =
  !sharedReportId &&
  !printReportId &&
  !isInternalSamplePdf &&
  !isInternalSampleLive &&
  !isPublicLegalRoute &&
  urlLooksLikePasswordReset();

// Base UI / @floating-ui portal teardown can throw "...reading 'getRootNode'"
// when a popover/tooltip/dropdown unmounts mid-flight (notably under dev HMR +
// the auth/data-load churn). It is BENIGN — the element is already gone. We
// (a) RECOVER from it in the ErrorBoundary rather than showing the crash
// screen, and (b) SUPPRESS it when it fires outside React (event / rAF dismiss
// handlers on already-unmounted portals) so it doesn't surface as an uncaught
// error or pop the Vite dev overlay. Narrowly matched on the message; a
// genuinely recurring error still surfaces past a lifetime cap. Pairs with the
// StrictMode-removal note below.
const FLOATING_UI_TEARDOWN = /getRootNode/;
function isBenignFloatingUiTeardown(error: unknown): boolean {
  const msg =
    (error as { message?: string } | null)?.message ??
    (typeof error === "string" ? error : "");
  return FLOATING_UI_TEARDOWN.test(msg);
}
let __floatingUiRecoveries = 0;
const MAX_FLOATING_UI_RECOVERIES = 8;

if (typeof window !== "undefined") {
  window.addEventListener(
    "error",
    (e) => {
      if (
        isBenignFloatingUiTeardown(e.error) ||
        isBenignFloatingUiTeardown(e.message)
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true,
  );
  window.addEventListener("unhandledrejection", (e) => {
    if (isBenignFloatingUiTeardown(e.reason)) e.preventDefault();
  });
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Recover from the benign Base UI/@floating-ui "getRootNode" teardown
    // (see note above) instead of showing the crash screen — the element is
    // already unmounted, so re-rendering the children succeeds. Lifetime-capped
    // so a genuinely recurring error still surfaces.
    if (
      isBenignFloatingUiTeardown(error) &&
      __floatingUiRecoveries < MAX_FLOATING_UI_RECOVERIES
    ) {
      __floatingUiRecoveries += 1;
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Benign floating-ui teardown — recovered above; log quietly and skip the
    // crash telemetry / errorInfo flip so it never reaches the crash screen.
    if (isBenignFloatingUiTeardown(error)) {
      // eslint-disable-next-line no-console
      console.warn(
        "[ErrorBoundary] recovered from benign floating-ui teardown (getRootNode).",
      );
      return;
    }
    // Always log the full info — production minification means the
    // wire form of these crashes is identical, so the componentStack
    // is the only way to localise render-loop bugs after the fact.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", {
      message: error.message,
      stack: (error.stack || "").slice(0, 4000),
      componentStack: (errorInfo.componentStack || "").slice(0, 8000),
      timestamp: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "ssr",
      buildHash:
        (window as unknown as { __BUILD_HASH__?: string }).__BUILD_HASH__ ??
        "unknown",
    });
    // Best-effort: persist for cross-session diagnosis. Never throw.
    void persistCrashTelemetry(error, errorInfo).catch(() => undefined);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 40,
            fontFamily: "system-ui",
            maxWidth: 600,
            margin: "80px auto",
          }}
        >
          <h1 style={{ fontSize: 24, marginBottom: 16 }}>
            Something went wrong
          </h1>
          <p style={{ color: "#666", marginBottom: 16 }}>
            The application encountered an unexpected error. Your work is
            auto-saved — try refreshing the page.
          </p>
          <pre
            style={{
              background: "#f5f5f5",
              padding: 16,
              borderRadius: 8,
              fontSize: 13,
              overflow: "auto",
              maxHeight: 200,
            }}
          >
            {this.state.error?.message}
          </pre>
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 20px",
                background: "#111",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              style={{
                padding: "8px 20px",
                background: "#fff",
                color: "#111",
                border: "1px solid #ccc",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Clear data &amp; reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Note: StrictMode removed — React 19 StrictMode double-invokes effects
// which causes Base UI (@base-ui/react) floating-ui portal teardown to crash
// with "getRootNode" on unmounted elements. Can re-enable when Base UI
// releases a fix for React 19 StrictMode compatibility.
const rootContainer = document.getElementById("root")!;
type RootContainer = HTMLElement & {
  __datacrateRoot?: ReturnType<typeof createRoot>;
};
const appRoot =
  (rootContainer as RootContainer).__datacrateRoot ?? createRoot(rootContainer);
(rootContainer as RootContainer).__datacrateRoot = appRoot;

const appTree = (
  <>
    <ErrorBoundary>
      {printReportId ? (
        // Print + shared report views use synchronous URL probes (not the
        // router) and live entirely outside the auth shell — keep them
        // outside BrowserRouter so they don't pay the router cost.
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <PrintReportView reportId={printReportId} />
        </Suspense>
      ) : isInternalSamplePdf ? (
        // TSE-3-FOLLOWUP-SERVER-SAMPLE-PDF — internal sample-PDF
        // render route. Same out-of-auth-shell discipline as
        // /print/{reportId}.
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <SampleReportPrintView />
        </Suspense>
      ) : isInternalSampleLive ? (
        // DEFECT-4 — internal LIVE-viewer render route. Same out-of-auth-shell
        // discipline as /internal/sample-pdf.
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <SampleLiveView />
        </Suspense>
      ) : sharedReportId ? (
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <SharedReportView shareId={sharedReportId} />
        </Suspense>
      ) : isPublicLegalRoute ? (
        // v1.12.8 — PUBLIC legal docs, OUTSIDE the auth shell (same discipline as
        // the print / sample routes). Own BrowserRouter so the pages' <Link>
        // cross-links (Home / Privacy / Terms) resolve; not wrapped in AuthGate.
        <BrowserRouter>
          <Suspense fallback={<RouteLoadingSkeleton />}>
            <Routes>
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      ) : isPasswordResetLanding ? (
        <PasswordResetScreen />
      ) : isMagicLinkLanding ? (
        <InviteLanding>
          <AuthGate>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthGate>
        </InviteLanding>
      ) : (
        <AuthGate>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthGate>
      )}
    </ErrorBoundary>
  </>
);

// Show startup progress before provider storage/network can delay redirects.
// A late redirect still updates Firebase auth; AuthGate owns claims and RBAC.
void mountWithBoundedAuthRedirect({
  resolveRedirect: handleAuthRedirect,
  renderLoading: () => appRoot.render(<RouteLoadingSkeleton />),
  renderApp: () => appRoot.render(appTree),
  onRedirectFailure: (error) => console.error("[auth] redirect startup failed", error),
});
